import { memo, useCallback, useRef, type FC } from "react";
import { Braces, Plus } from "lucide-react";
import type { TextAreaRef } from "antd/es/input/TextArea";
import { useTranslation } from "react-i18next";
import AudienceListSelect from "src/components/reusable/AudienceListSelect/AudienceListSelect";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import StyledTextArea from "src/components/reusable/StyledTextArea";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import type { WhatsAppMediaType } from "src/services/WhatsAppCampaignAPI";
import type { WhatsAppAiFilledField } from "../WhatsAppCampaignTypes";
import { getRecipientCount } from "src/utils/audienceListLabels";
import { formatNumber } from "src/utils/numberFormat";
import CampaignFormField from "../../../shared/CampaignFormField";
import CampaignStepCard from "../../../shared/CampaignStepCard";
import AiFilledBadge from "../components/AiFilledBadge";
import EmojiPickerButton from "../components/EmojiPickerButton";
import AiPromptBox from "../components/AiPromptBox";
import MediaAttachmentField from "../components/MediaAttachmentField";
import TemplateButtonsEditor from "../components/TemplateButtonsEditor";
import TemplateFormPreview from "../components/TemplateFormPreview";
import type { WhatsAppTemplateFormState } from "../hooks/UseWhatsAppTemplateForm";
import {
  DEFAULT_SAMPLE_COLUMNS,
  TEMPLATE_FORM_LIMITS,
  TEMPLATE_LANGUAGES,
  WHATSAPP_EDITOR_KEY,
} from "../WhatsAppCampaignConstants";
import {
  getBodyVariableIndexes,
  getSuggestedColumns,
  getNextVariableIndex,
  insertAt,
  wrapSelection,
} from "../WhatsAppTemplateFormUtils";

const FORM_KEY = `${WHATSAPP_EDITOR_KEY}.form`;
const POPUP_CLASS = "campaign-flow__popup";
const MEDIA_OPTIONS: (WhatsAppMediaType | "none")[] = ["none", "image", "video", "document"];
const FORMAT_MARKERS = [
  { key: "bold", label: "B", marker: "*" },
  { key: "italic", label: "I", marker: "_" },
  { key: "strike", label: "S", marker: "~" },
  { key: "code", label: "</>", marker: "```" },
];

interface WhatsAppTemplateFormStepProps {
  form: WhatsAppTemplateFormState;
  aiPromptInCard?: boolean;
}

const WhatsAppTemplateFormStep: FC<WhatsAppTemplateFormStepProps> = ({ form, aiPromptInCard = false }) => {
  const { t, i18n } = useTranslation();
  const bodyRef = useRef<TextAreaRef>(null);
  const {
    values,
    errors,
    showErrors,
    audienceOptions,
    isListsLoading,
    audience,
    media,
    isReadOnly,
    isAiMode,
    aiFilled,
    update,
  } = form;
  const aiBadge = (field: WhatsAppAiFilledField) =>
    aiFilled.includes(field) ? <AiFilledBadge /> : undefined;
  const recipientCount = getRecipientCount(audienceOptions, values.sendTo);
  const variableIndexes = getBodyVariableIndexes(values.body);
  const fieldError = (key: string | undefined) => (showErrors && key ? t(key) : undefined);
  const counter = (length: number, max: number, hint?: string) => (
    <span className="wa-form__counter">
      <span className="campaign-flow-field__helper">{hint}</span>
      <span className="campaign-flow-field__counter">{`${length}/${max}`}</span>
    </span>
  );

  const editBody = useCallback(
    (edit: (_text: string, _start: number, _end: number) => { value: string; cursor: number }) => {
      const element = bodyRef.current?.resizableTextArea?.textArea;
      const start = element?.selectionStart ?? values.body.length;
      const end = element?.selectionEnd ?? values.body.length;
      const next = edit(values.body, start, end);
      update("body", next.value.slice(0, TEMPLATE_FORM_LIMITS.body));
      requestAnimationFrame(() => {
        element?.focus();
        element?.setSelectionRange(next.cursor, next.cursor);
      });
    },
    [update, values.body]
  );

  const sampleColumns = audience.columns.length ? audience.columns : DEFAULT_SAMPLE_COLUMNS;

  const getSampleOptions = (index: number) => {
    const toOption = (column: string) => ({ value: column, label: column });
    const suggested = getSuggestedColumns(values.body, index, sampleColumns);
    if (!suggested.length) {
      return sampleColumns.map(toOption);
    }
    return [
      { label: t(`${FORM_KEY}.samples.suggested`), options: suggested.map(toOption) },
      {
        label: t(`${FORM_KEY}.samples.allColumns`),
        options: sampleColumns.filter(column => !suggested.includes(column)).map(toOption),
      },
    ];
  };

  const insertEmoji = useCallback(
    (emoji: string) => editBody((text, start) => insertAt(text, start, emoji)),
    [editBody]
  );

  const addVariable = useCallback(
    () =>
      editBody((text, start) =>
        insertAt(text, start, `{{${getNextVariableIndex(text)}}}`)
      ),
    [editBody]
  );

  const aiPrompt = isAiMode && (
    <AiPromptBox isGenerating={form.isGenerating} onGenerate={form.generateFromPrompt} />
  );

  return (
    <>
      {!aiPromptInCard && aiPrompt}
    <CampaignStepCard
      wide
      title={t(`${FORM_KEY}.title`)}
      subtitle={t(`${FORM_KEY}.subtitle`)}
      className="wa-form-page"
      titleSuffix={<span className="wa-form-badge">{t(`${FORM_KEY}.badge`)}</span>}
    >
      {aiPromptInCard && aiPrompt}
      <div className="wa-form">
        <fieldset className="wa-form__main" disabled={isReadOnly}>
          <section className="wa-form-card">
            <h3 className="wa-form-section__title">{t(`${FORM_KEY}.recipients.title`)}</h3>
            <p className="wa-form-section__subtitle">{t(`${FORM_KEY}.recipients.subtitle`)}</p>
            <CampaignFormField
              label={t("campaignFlow.createCampaignNew.sendTo")}
              htmlFor="wa-form-send-to"
              required
              badge={aiBadge("sendTo")}
              hasError={Boolean(fieldError(errors.sendTo))}
              errorMessage={fieldError(errors.sendTo)}
            >
              <AudienceListSelect
                id="wa-form-send-to"
                value={values.sendTo}
                options={audienceOptions}
                onChange={value => update("sendTo", value)}
                loading={isListsLoading}
                placeholder={t("campaignFlow.createCampaignNew.selectAudience")}
                popupClassName={POPUP_CLASS}
                showSelectedIcon
              />
            </CampaignFormField>
            {values.sendTo.length > 0 && recipientCount !== null && (
              <p className="campaign-flow-field__helper">
                {t(`${FORM_KEY}.recipients.count`, {
                  count: recipientCount,
                  formattedCount: formatNumber(recipientCount, {}, i18n.language),
                })}
              </p>
            )}
          </section>

          <section className="wa-form-card">
            <h3 className="wa-form-section__title">{t(`${FORM_KEY}.details.title`)}</h3>
            <p className="wa-form-section__subtitle">{t(`${FORM_KEY}.details.subtitle`)}</p>
            <div className="campaign-flow__field-row">
              <CampaignFormField
                label={t(`${FORM_KEY}.details.name`)}
                htmlFor="wa-form-name"
                required
                badge={aiBadge("name")}
                hasError={Boolean(fieldError(errors.name))}
                errorMessage={fieldError(errors.name)}
                counter={counter(
                  values.name.length,
                  TEMPLATE_FORM_LIMITS.name,
                  fieldError(errors.name) ? undefined : t(`${FORM_KEY}.details.nameHint`)
                )}
              >
                <StyledTextInput
                  id="wa-form-name"
                  value={values.name}
                  maxLength={TEMPLATE_FORM_LIMITS.name}
                  placeholder={t(`${FORM_KEY}.details.namePlaceholder`)}
                  onChange={event => update("name", event.target.value)}
                  className="campaign-flow__input"
                />
              </CampaignFormField>
              <CampaignFormField
                label={t(`${FORM_KEY}.details.language`)}
                htmlFor="wa-form-language"
                required
                badge={aiBadge("language")}
                hasError={Boolean(fieldError(errors.language))}
                errorMessage={fieldError(errors.language)}
                helperText={t(`${FORM_KEY}.details.languageHint`)}
              >
                <StyledDropdown
                  id="wa-form-language"
                  value={values.language}
                  options={TEMPLATE_LANGUAGES}
                  onChange={(value: string) => update("language", value)}
                  placeholder={t(`${FORM_KEY}.details.languagePlaceholder`)}
                  className="campaign-flow__select"
                  popupClassName={POPUP_CLASS}
                />
              </CampaignFormField>
            </div>
          </section>

          <section className="wa-form-card">
            <h3 className="wa-form-section__title">{t(`${FORM_KEY}.content.title`)}</h3>
            <p className="wa-form-section__subtitle">{t(`${FORM_KEY}.content.subtitle`)}</p>

            <CampaignFormField
              label={t(`${FORM_KEY}.content.media`)}
              hint={t(`${FORM_KEY}.optional`)}
              badge={aiBadge("mediaType")}
              labelId="wa-form-media-label"
            >
              <div
                className="wa-live-preview__modes wa-form__media-tabs"
                role="group"
                aria-labelledby="wa-form-media-label"
              >
                {MEDIA_OPTIONS.map(option => {
                  const isActive = (values.mediaType ?? "none") === option;
                  return (
                    <button
                      key={option}
                      type="button"
                      aria-pressed={isActive}
                      className={`wa-live-preview__mode ${
                        isActive ? "wa-live-preview__mode--active" : ""
                      }`}
                      onClick={() => form.changeMediaType(option === "none" ? null : option)}
                    >
                      {t(`${FORM_KEY}.content.mediaTypes.${option}`)}
                    </button>
                  );
                })}
              </div>
            </CampaignFormField>

            {values.mediaType ? (
              <>
                <MediaAttachmentField
                  mediaType={values.mediaType}
                  media={media}
                  onAttach={form.attachMedia}
                  onRemove={form.removeMedia}
                />
                {fieldError(errors.media) && !media.attachment && (
                  <span role="alert" className="campaign-flow-field__error">
                    {fieldError(errors.media)}
                  </span>
                )}
              </>
            ) : (
              <CampaignFormField
                label={t(`${FORM_KEY}.content.header`)}
                hint={t(`${FORM_KEY}.optional`)}
                badge={aiBadge("headerText")}
                htmlFor="wa-form-header"
                counter={counter(values.headerText.length, TEMPLATE_FORM_LIMITS.header)}
              >
                <StyledTextInput
                  id="wa-form-header"
                  value={values.headerText}
                  maxLength={TEMPLATE_FORM_LIMITS.header}
                  placeholder={t(`${FORM_KEY}.content.headerPlaceholder`)}
                  onChange={event => update("headerText", event.target.value)}
                  className="campaign-flow__input"
                />
              </CampaignFormField>
            )}

            <CampaignFormField
              label={t(`${FORM_KEY}.content.body`)}
              htmlFor="wa-form-body"
              required
              badge={aiBadge("body")}
              hasError={Boolean(fieldError(errors.body))}
              errorMessage={fieldError(errors.body)}
              counter={counter(
                values.body.length,
                TEMPLATE_FORM_LIMITS.body,
                fieldError(errors.body) ? undefined : t(`${FORM_KEY}.content.bodyHint`)
              )}
            >
              <div
                className={`wa-form__editor ${
                  fieldError(errors.body) ? "wa-form__editor--error" : ""
                }`}
              >
                <div className="wa-form__toolbar" role="toolbar" aria-label={t(`${FORM_KEY}.content.formatting`)}>
                  <div className="wa-form__toolbar-group">
                    <EmojiPickerButton onPick={insertEmoji} />
                    {FORMAT_MARKERS.map(({ key, label, marker }) => (
                      <button
                        key={key}
                        type="button"
                        className="wa-form__toolbar-button"
                        aria-label={t(`${FORM_KEY}.content.format.${key}`)}
                        onClick={() =>
                          editBody((text, start, end) => wrapSelection(text, start, end, marker))
                        }
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <button type="button" className="wa-form__add-variable" onClick={addVariable}>
                    <Plus size={14} aria-hidden />
                    {t(`${FORM_KEY}.content.addVariable`)}
                  </button>
                </div>
                <StyledTextArea
                  id="wa-form-body"
                  ref={bodyRef}
                  value={values.body}
                  maxLength={TEMPLATE_FORM_LIMITS.body}
                  rows={5}
                  placeholder={t(`${FORM_KEY}.content.bodyPlaceholder`)}
                  onChange={event => update("body", event.target.value)}
                  className="campaign-flow__input wa-form__textarea"
                  aria-invalid={Boolean(fieldError(errors.body))}
                />
              </div>
            </CampaignFormField>

            <CampaignFormField
              label={t(`${FORM_KEY}.content.footer`)}
              hint={t(`${FORM_KEY}.optional`)}
              badge={aiBadge("footer")}
              htmlFor="wa-form-footer"
              counter={counter(
                values.footer.length,
                TEMPLATE_FORM_LIMITS.footer,
                t(`${FORM_KEY}.content.footerHint`)
              )}
            >
              <StyledTextInput
                id="wa-form-footer"
                value={values.footer}
                maxLength={TEMPLATE_FORM_LIMITS.footer}
                placeholder={t(`${FORM_KEY}.content.footerPlaceholder`)}
                onChange={event => update("footer", event.target.value)}
                className="campaign-flow__input"
              />
            </CampaignFormField>
          </section>

          <section className="wa-form-card">
            <h3 className="wa-form-section__title">{t(`${FORM_KEY}.samples.title`)}</h3>
            <p className="wa-form-section__subtitle">{t(`${FORM_KEY}.samples.subtitle`)}</p>
            {variableIndexes.length === 0 ? (
              <p className="wa-template-library__empty">
                <Braces size={14} aria-hidden /> {t(`${FORM_KEY}.samples.empty`)}
              </p>
            ) : (
              variableIndexes.map(index => (
                <div key={index} className="wa-form__sample">
                  <span className="wa-parameter__tag">{`{{${index}}}`}</span>
                  <CampaignFormField
                    label={t(`${FORM_KEY}.samples.label`, { index })}
                    htmlFor={`wa-form-sample-${index}`}
                    hasError={showErrors && errors.samples.includes(index)}
                    errorMessage={t(`${FORM_KEY}.errors.sample`)}
                  >
                    <StyledDropdown
                      id={`wa-form-sample-${index}`}
                      value={values.samples[index]}
                      options={getSampleOptions(index)}
                      onChange={(value: string) => form.updateSample(index, value)}
                      loading={audience.status === "loading"}
                      placeholder={t(`${FORM_KEY}.samples.placeholder`)}
                      className="campaign-flow__select"
                      popupClassName={POPUP_CLASS}
                    />
                  </CampaignFormField>
                </div>
              ))
            )}
          </section>

          <TemplateButtonsEditor
            buttons={values.buttons}
            errors={errors.buttons}
            showErrors={showErrors}
            onAdd={form.addButton}
            onUpdate={form.updateButton}
            onChangeType={form.changeButtonType}
            onRemove={form.removeButton}
          />
        </fieldset>

        <TemplateFormPreview values={values} errors={errors} attachment={media.attachment} />
      </div>
    </CampaignStepCard>
    </>
  );
};

export default memo(WhatsAppTemplateFormStep);
